import { useEffect } from 'react';

interface SEOHelmetProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleTags?: string[];
}

export default function SEOHelmet({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleTags,
}: SEOHelmetProps) {
  useEffect(() => {
    const siteName = '91安全指南';

    document.title = `${title} | ${siteName}`;

    function setMeta(nameOrProperty: string, content: string, isProperty = false) {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${nameOrProperty}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, nameOrProperty);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    }

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    setMeta('og:title', `${title} | ${siteName}`, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    if (canonicalUrl) setMeta('og:url', canonicalUrl, true);
    if (ogImage) setMeta('og:image', ogImage, true);
    setMeta('og:locale', 'zh_CN', true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', `${title} | ${siteName}`);
    setMeta('twitter:description', description);

    if (articlePublishedTime) setMeta('article:published_time', articlePublishedTime);
    if (articleModifiedTime) setMeta('article:modified_time', articleModifiedTime);
    if (articleAuthor) setMeta('article:author', articleAuthor);
    if (articleTags && articleTags.length > 0) {
      articleTags.forEach((tag) => {
        let el = document.querySelector(`meta[property="article:tag"][content="${tag}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute('property', 'article:tag');
          el.setAttribute('content', tag);
          document.head.appendChild(el);
        }
      });
    }

    return () => {};
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, articlePublishedTime, articleModifiedTime, articleAuthor, articleTags]);

  return null;
}